import { type MaintenanceWorkOrder } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { useTrashMaintenanceWorkOrder } from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import { maintenanceWorkOrderEditPath } from '@/products/maintenance/maintenance-paths';

const EDITABLE = new Set<MaintenanceWorkOrder['status']>([
  'planned',
  'assigned',
  'in_progress',
  'finalizing',
]);

type OrderRow = Pick<MaintenanceWorkOrder, 'id' | 'orderNumber' | 'status' | 'protocolHasContent'>;

export function MaintenanceWorkOrderSelectionBar({
  orders,
  selectedIds,
  onClear,
}: {
  orders: readonly OrderRow[];
  selectedIds: ReadonlySet<string>;
  onClear: () => void;
}) {
  const selected = orders.filter((order) => selectedIds.has(order.id));
  const trash = useTrashMaintenanceWorkOrder();
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [pending, setPending] = useState(false);
  const single = selected.length === 1 ? selected[0] : undefined;
  const canEdit = single != null && EDITABLE.has(single.status);

  if (selected.length === 0) {
    return null;
  }

  async function runTrash() {
    if (selected.length === 0 || pending) {
      return;
    }
    setPending(true);
    const failures: string[] = [];
    for (const row of selected) {
      try {
        await trash.mutateAsync(row.id);
      } catch (err: unknown) {
        const message = err instanceof ApiRequestError ? err.message : 'fehlgeschlagen';
        failures.push(`${row.orderNumber}: ${message}`);
      }
    }
    setPending(false);
    setConfirmTrash(false);
    onClear();
    if (failures.length > 0) {
      toast.error(failures.join(' '));
      return;
    }
    toast.success(
      selected.length === 1
        ? 'Auftrag in den Papierkorb gelegt.'
        : `${selected.length} Aufträge in den Papierkorb gelegt.`,
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">{selected.length} ausgewählt</p>
        {canEdit && single ? (
          <Button asChild variant="outline" className="min-h-11">
            <Link to={maintenanceWorkOrderEditPath(single.id)}>Bearbeiten</Link>
          </Button>
        ) : (
          <Button type="button" variant="outline" className="min-h-11" disabled>
            Bearbeiten
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => setConfirmTrash(true)}
        >
          In Papierkorb
        </Button>
        <Button type="button" variant="ghost" className="min-h-11" onClick={onClear}>
          Auswahl aufheben
        </Button>
      </div>

      <AlertDialog
        open={confirmTrash}
        onOpenChange={(open) => {
          if (!open && !pending) {
            setConfirmTrash(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selected.length === 1
                ? `Auftrag ${selected[0]?.orderNumber ?? ''} in den Papierkorb legen?`
                : `${selected.length} Aufträge in den Papierkorb legen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selected.length === 1
                ? 'Der Auftrag wird aus Planung, Archiv und der Monteur-Liste genommen. Im Papierkorb kann er wiederhergestellt oder endgültig gelöscht werden.'
                : 'Die Aufträge werden aus Planung, Archiv und der Monteur-Liste genommen. Im Papierkorb können sie wiederhergestellt oder endgültig gelöscht werden.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={(event) => {
                event.preventDefault();
                void runTrash();
              }}
            >
              In Papierkorb
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
